<script setup lang="ts">
import { computed } from 'vue'

/**
 * The first carrier of an entry's kind - four states, because `kind` is optional in the
 * contract. Drawn on a 16 grid at a constant 1.7-unit stroke with butt caps and mitre
 * joins, so it reads as the same hand as the mark rather than a stock glyph. All four are
 * distinguishable in greyscale, by form alone: income and expense share a ground line and
 * differ only in where the arrowhead sits, never by an arrowhead rotation alone. This
 * never changes with the amount's sign - see `formatMoney`'s `sign` option for
 * that, a separate carrier.
 */
const props = defineProps<{
  kind: 'INCOME' | 'EXPENSE' | 'TRANSFER' | undefined
  label: string
}>()

const TONE_CLASSES = {
  INCOME: 'text-[color:var(--color-income)]',
  EXPENSE: 'text-[color:var(--color-expense)]',
  TRANSFER: 'text-[color:var(--color-transfer)]',
} as const
const toneClass = computed(() =>
  props.kind === undefined ? 'text-[color:var(--color-text-muted)]' : TONE_CLASSES[props.kind],
)
</script>

<template>
  <svg
    width="15"
    height="15"
    viewBox="0 0 16 16"
    role="img"
    :aria-label="label"
    class="block shrink-0"
    :class="toneClass"
  >
    <g
      v-if="kind === 'INCOME'"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="butt"
      stroke-linejoin="miter"
    >
      <path d="M2.8 13.4h10.4" />
      <path d="M8 2.6v7" />
      <path d="M5.2 6.9 8 9.7 10.8 6.9" />
    </g>
    <g
      v-else-if="kind === 'EXPENSE'"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="butt"
      stroke-linejoin="miter"
    >
      <path d="M2.8 13.4h10.4" />
      <path d="M8 10.4v-7" />
      <path d="M5.2 6.1 8 3.3 10.8 6.1" />
    </g>
    <g
      v-else-if="kind === 'TRANSFER'"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="butt"
      stroke-linejoin="miter"
    >
      <path d="M2.4 5.4h10.1" />
      <path d="M9.9 2.9 12.8 5.4 9.9 7.9" />
      <path d="M13.6 10.6H3.5" />
      <path d="M6.1 8.1 3.2 10.6 6.1 13.1" />
    </g>
    <circle v-else cx="8" cy="8" r="3.5" fill="none" stroke="currentColor" stroke-width="1.7" />
  </svg>
</template>
